/**
 * SwingBeamVFXHandler — transient stroke-drawn combat overlays, extracted
 * from VFXSystem (decomposition arc, 2026-06-12).
 *
 *  - `vfx:melee_swing`   → body-anchored swing sized to the reach
 *                          (MeleeSwingRenderer; left-facing mirrors)
 *  - `vfx:hitscan_beam`  → core + glow line between two points
 *
 * Both draw geometry around the LOCAL ORIGIN and carry world position via
 * setPosition (the ShapeSpawner Lane-4 invariant) — drawing at absolute
 * coords makes any origin-anchored scale tween TRANSLATE the shape (the
 * 2026-06-12 "-60° stray projectile" bug). Regression test:
 * VFXSystem.meleeSwing.test.ts.
 */
import { createGraphics } from '../../rendering/utils/Graphics';
import { MELEE_SWING_CONFIG } from '../../../config/abilities/meleeSwing.config';
import type { EventPayloadMap } from '../../../shared/EventBus/EventPayloadMap';
import { drawMeleeSwing, drawAreaCone } from '../melee/MeleeSwingRenderer';
import { getMotionPrefs } from '../../systems/accessibility/motionPrefs';
import { ServiceRegistry } from '../../core/ServiceRegistry';
import { findMobPosition } from './entityPositions';

type MeleeSwingEvent = EventPayloadMap['vfx:melee_swing'];
type MobProjectileEvent = EventPayloadMap['vfx:mob_projectile'];

/** A swing target's centre: a remote player sprite, else a mob/NPC. */
function findSwingTargetPosition(targetId: string): { x: number; y: number } | null {
  try {
    const entitySystem = ServiceRegistry.getInstance().get('EntitySystem', { optional: true });
    const remote = entitySystem?.getRemotePlayerManager?.()?.getSprite?.(targetId);
    if (remote) return { x: remote.x, y: remote.y };
  } catch { /* VFX is non-critical */ }
  return findMobPosition(targetId);
}
import { HITSCAN_BEAM_CONFIG } from '../../../config/abilities/hitscanBeam.config';

export class SwingBeamVFXHandler {
  constructor(private getScene: () => unknown) {}

  /** Install subscriptions via the owner's tracked `sub` helper. */
  register(sub: (event: string, handler: (data: any) => void) => void): void {
    sub('vfx:melee_swing', (data) => this.onMeleeSwing(data));
    sub('vfx:hitscan_beam', (data) => this.onHitscanBeam(data));
    sub('vfx:mob_projectile', (data) => this.onMobProjectile(data));
    sub('vfx:area_cone', (data) => drawAreaCone(this.getScene(), data));
  }

  private onMeleeSwing(data: MeleeSwingEvent): void {
    // Lane AZ: a body-anchored smear (or a per-ability look) sized to the
    // reach — MeleeSwingRenderer owns the shapes; this resolves the contact.
    const style = data.style ?? 'smear';
    let contact: { x: number; y: number } | null = null;
    if (style === 'lightning' && data.targetId) {
      const p = findSwingTargetPosition(data.targetId);
      // Only a target actually inside the reach (+ slack) becomes the contact;
      // otherwise the bolt stops at the reach instead of crossing the map.
      if (p && Math.hypot(p.x - data.x, p.y - data.y) <= data.arcRadius * 1.25) contact = p;
    }
    drawMeleeSwing(this.getScene(), {
      x: data.x,
      y: data.y,
      isRight: data.isRight,
      color: data.color ?? MELEE_SWING_CONFIG.color,
      arcRadius: data.arcRadius ?? 60,
      arcAngle: data.arcAngle ?? MELEE_SWING_CONFIG.arcAngle,
      arcSpread: data.arcSpread ?? MELEE_SWING_CONFIG.arcSpread,
      duration: data.duration ?? MELEE_SWING_CONFIG.durationMs,
      style,
      contact,
      reducedMotion: getMotionPrefs().reduceMotion,
      cone: data.cone,
    });
  }

  private onHitscanBeam(data: { startX: number; startY: number; endX: number; endY: number; color: number; width: number; duration: number }): void {
    const cfg = HITSCAN_BEAM_CONFIG;
    const color = data.color ?? cfg.color;
    const w = data.width ?? cfg.width;
    const duration = data.duration ?? cfg.durationMs;

    // Core line + glow line (wider, dimmer, drawn underneath)
    const core = createGraphics(this.getScene(), { depth: cfg.coreDepth });
    core.strokeLine(data.startX, data.startY, data.endX, data.endY, color, w, cfg.coreAlpha);

    // Glow line through the bloom layer (M1) — additive + blur = a real
    // luminous beam (chain lightning, hitscan). Falls back to flat if the
    // glow layer isn't up.
    const glow = createGraphics(this.getScene(), { depth: cfg.glowDepth, glow: true });
    glow.strokeLine(data.startX, data.startY, data.endX, data.endY, color, w * cfg.glowWidthMultiplier, cfg.glowAlpha);

    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      core.setAlpha(1 - t);
      glow.setAlpha(1 - t);
      if (t < 1) requestAnimationFrame(tick);
      else { core.destroy(); glow.destroy(); }
    };
    requestAnimationFrame(tick);
  }

  private onMobProjectile(data: MobProjectileEvent): void {
    const cfg = HITSCAN_BEAM_CONFIG;
    const color = data.color ?? cfg.color;
    const coreColor = data.coreColor ?? 0xffffff;
    const r = Math.max(4, data.radius ?? 10);
    const duration = Math.max(60, data.durationMs ?? 240);
    const arcHeight = data.arcHeightPx ?? 0;
    const dx = data.endX - data.startX;
    const dy = data.endY - data.startY;
    const len = Math.hypot(dx, dy) || 1;
    const ux = dx / len;
    const uy = dy / len;

    const glow = createGraphics(this.getScene(), { depth: cfg.glowDepth, glow: true });
    glow.fillCircle?.(0, 0, r * 2.1, color, 0.38);
    glow.fillCircle?.(0, 0, r * 1.35, color, 0.65);
    glow.strokeLine(-ux * r * 2.8, -uy * r * 2.8, 0, 0, color, r * 1.25, 0.55);
    glow.setPosition(data.startX, data.startY);

    const core = createGraphics(this.getScene(), { depth: cfg.coreDepth });
    core.fillCircle?.(0, 0, r, color, 0.95);
    core.strokeCircle?.(0, 0, r * 1.15, coreColor, 2, 0.85);
    core.fillCircle?.(0, 0, r * 0.5, coreColor, 0.98);
    core.strokeLine(-ux * r * 2.2, -uy * r * 2.2, 0, 0, coreColor, Math.max(2, r * 0.45), 0.85);
    if (data.style === 'eye_bolt') {
      core.strokeCircle?.(0, 0, r * 1.45, color, 1.5, 0.78);
      core.fillCircle?.(ux * r * 0.2, uy * r * 0.2, r * 0.28, 0x1a0a2a, 0.95);
    }
    core.setPosition(data.startX, data.startY);

    const startTime = performance.now();
    const tick = () => {
      const elapsed = performance.now() - startTime;
      const t = Math.min(elapsed / duration, 1);
      const x = data.startX + dx * t;
      const y = data.startY + dy * t - (arcHeight > 0 ? Math.sin(t * Math.PI) * arcHeight : 0);
      const alpha = t > 0.85 ? Math.max(0, (1 - t) / 0.15) : 1;
      core.setPosition(x, y);
      glow.setPosition(x, y);
      core.setAlpha(alpha);
      glow.setAlpha(alpha);
      if (t < 1) requestAnimationFrame(tick);
      else { core.destroy(); glow.destroy(); }
    };
    requestAnimationFrame(tick);
  }
}
